import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CopyPlus, Pencil, Trash2 } from 'lucide-react';
import {
  termsApi,
  TERMS_STATUS_LABEL,
  TERMS_TARGET_LABEL,
  TERMS_TYPE_LABEL,
  type Terms,
  type TermsStatus,
} from '@/services/api';
import { sanitizeTermsHtml } from '@/utils/sanitizeHtml';
import {
  DescList,
  PageHeader,
  Section,
  StatusBadge,
  LoadingState,
  ErrorState,
  type BadgeTone,
} from '@/components/common';

const STATUS_TONE: Record<TermsStatus, BadgeTone> = {
  ACTIVE: 'success',
  SCHEDULED: 'warning',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

const formatDateTime = (dt?: string | null) =>
  dt ? dt.slice(0, 16).replace('T', ' ') : '-';

export function TermsDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const numericId = Number(id);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['terms', numericId],
    queryFn: () => termsApi.getTermsDetail(numericId),
    enabled: Number.isFinite(numericId),
    staleTime: 30 * 1000,
  });

  // 같은 대상·유형의 전체 버전 이력 — 상세를 열면 바로 계보가 보이도록
  const { data: history } = useQuery({
    queryKey: ['terms-history', data?.targetRole, data?.termsType],
    queryFn: () =>
      termsApi.getVersionHistory(
        data!.targetRole,
        data!.termsType,
      ),
    enabled: !!data,
    staleTime: 30 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: () => termsApi.deleteTerms(numericId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['terms'] });
      alert('약관이 삭제되었습니다.');
      navigate('/terms');
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    },
  });

  if (isLoading) {
    return <LoadingState />;
  }
  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }
  if (!data) {
    return <ErrorState error={new Error('약관을 찾을 수 없습니다.')} />;
  }

  const editable = data.status === 'DRAFT' || data.status === 'SCHEDULED';

  return (
    <div className="space-y-4">
      <PageHeader
        title="약관 상세"
        subtitle={`${TERMS_TARGET_LABEL[data.targetRole]} · ${TERMS_TYPE_LABEL[data.termsType]} · ${data.termsVersion}`}
        right={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft size={16} />
              뒤로가기
            </button>
            <button
              type="button"
              onClick={() => navigate(`/terms/new?from=${data.id}`)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CopyPlus size={16} />
              이 버전으로 새 버전 만들기
            </button>
            {editable && (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/terms/${data.id}/edit`)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Pencil size={16} />
                  수정
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `약관을 삭제하시겠습니까?\n${data.termsTitle} (${data.termsVersion})`,
                      )
                    ) {
                      deleteMutation.mutate();
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                >
                  <Trash2 size={16} />
                  삭제
                </button>
              </>
            )}
          </div>
        }
      />

      <Section title="기본 정보">
        <DescList
          items={[
            {
              label: '상태',
              value: (
                <StatusBadge tone={STATUS_TONE[data.status]}>
                  {TERMS_STATUS_LABEL[data.status]}
                </StatusBadge>
              ),
            },
            { label: '제목', value: data.termsTitle },
            { label: '버전', value: data.termsVersion },
            { label: '적용 대상', value: TERMS_TARGET_LABEL[data.targetRole] },
            { label: '약관 유형', value: TERMS_TYPE_LABEL[data.termsType] },
            { label: '필수 동의', value: data.isRequired ? '필수' : '선택' },
            { label: '시행일', value: formatDateTime(data.effectiveAt) },
            { label: '고지 시작일', value: formatDateTime(data.noticeAt) },
            {
              label: '동의자 수',
              value:
                data.agreedCount != null
                  ? `${data.agreedCount.toLocaleString()}명`
                  : '-',
            },
            { label: '등록일', value: formatDateTime(data.createdAt) },
            { label: '최종 수정일', value: formatDateTime(data.updatedAt) },
          ]}
        />
        {!editable && (
          <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
            이미 시행된 버전이라 수정·삭제할 수 없습니다. 문구를 바꾸려면 새 버전을
            등록하세요. (동의 기록이 이 버전을 가리키고 있습니다)
          </p>
        )}
      </Section>

      {data.changeSummary && (
        <Section title="변경 요약">
          <pre className="whitespace-pre-wrap break-words font-sans text-sm text-gray-800">
            {data.changeSummary}
          </pre>
        </Section>
      )}

      <Section title="본문">
        {data.termsUrl && (
          <p className="mb-3 text-xs text-gray-500">
            원문 파일:{' '}
            <a
              href={data.termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline"
            >
              {data.termsUrl}
            </a>
          </p>
        )}
        <div
          className="announcement-rich-content max-w-none text-sm leading-relaxed text-gray-800"
          dangerouslySetInnerHTML={{
            __html: sanitizeTermsHtml(data.contentHtml ?? ''),
          }}
        />
      </Section>

      <Section title="버전 이력">
        {!history || history.length === 0 ? (
          <p className="text-sm text-gray-500">이력이 없습니다.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {history.map((v: Terms) => (
              <li
                key={v.id}
                className={`flex flex-wrap items-center gap-3 py-2.5 ${
                  v.id === data.id ? 'bg-primary-50/60 -mx-2 rounded px-2' : ''
                }`}
              >
                <StatusBadge tone={STATUS_TONE[v.status]}>
                  {TERMS_STATUS_LABEL[v.status]}
                </StatusBadge>
                <span className="w-20 text-sm font-medium text-gray-900">
                  {v.termsVersion}
                </span>
                <span className="w-40 text-sm text-gray-600">
                  시행 {formatDateTime(v.effectiveAt)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-700">
                  {v.termsTitle}
                </span>
                {v.id === data.id ? (
                  <span className="text-xs text-gray-500">보는 중</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate(`/terms/${v.id}`)}
                    className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    보기
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
